// ------------------------------------------------------------------------------
// ImageVirtualList
// author: mudas
// created: 2025/2/16
// ------------------------------------------------------------------------------

import React, { FC, useEffect } from 'react';

import { VirtuosoGrid, VirtuosoGridProps } from 'react-virtuoso';
import { Spin } from '@fuxi/eevee-ui';
import PreviewImage, { IProps as PreviewImageProps } from '../upload/PreviewImage';

import cx from './ImageVirtualList.module.css';
import { SocialArtworkStatusEnum } from 'common/types';

export interface IProps
  extends Omit<
    VirtuosoGridProps<{ id: string; url: string; artWorkStatus?: SocialArtworkStatusEnum }>,
    'selected' | 'onSelect'
  > {
  className?: string;

  // 列表属性显示加载提示
  loading?: boolean;
  showLabel?: boolean;

  // 删除时是否显示气泡确认提示
  showDeleteConfirm?: boolean;

  // 点击选定当前图片（未设置该回调方法则不显示选择框）
  selected?: string[];
  onDelete?: PreviewImageProps['onDelete'];
  onSelect?: PreviewImageProps['onSelect'];
  onPreview?: PreviewImageProps['onPreview'];
  onImportFineWork?: PreviewImageProps['onImportFineWork'];
  onBatchModePreview?: PreviewImageProps['onBatchModePreview'];
}

const ImageVirtualList: FC<IProps> = (props) => {
  const {
    className,
    loading,
    data,
    totalCount,
    endReached,
    selected,
    showDeleteConfirm,
    onSelect,
    onDelete,
    onPreview,
    onImportFineWork,
    onBatchModePreview,
    showLabel,
    ...otherProps
  } = props;

  return (
    <div className={cx('image-virtual-list', className)}>
      <VirtuosoGrid
        className="w-full h-full"
        data={data ?? []}
        totalCount={totalCount ?? 0}
        overscan={180} // 末尾保持一行在屏幕外，以避免显示空白
        listClassName={cx('list-wrapper')}
        itemClassName={cx('list-item')}
        components={{
          Footer: () =>
            loading && (
              <div className="p-8 flex items-center justify-center gap-2">
                <Spin size="small" />
                <span>加载中...</span>
              </div>
            ),
        }}
        itemContent={(index, item) => {
          return (
            item && (
              <PreviewImage
                key={item.id}
                uid={item.id}
                url={item.url}
                showLabel={showLabel}
                artWorkStatus={item?.artWorkStatus}
                showDeleteConfirm={showDeleteConfirm}
                selected={{ uid: item.id, value: !!selected?.includes(item.id) }}
                onSelect={onSelect}
                onDelete={onDelete}
                onPreview={onPreview}
                onImportFineWork={onImportFineWork}
                onBatchModePreview={onBatchModePreview}
              />
            )
          );
        }}
        endReached={(index) => !loading && endReached?.(index)}
        {...otherProps}
      />
    </div>
  );
};

export default ImageVirtualList;
